import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, InlineAlert } from '@scramble/ui';
import { useIsMobile } from '@scramble/hooks';
import { prettyMoney } from '@scramble/utils';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { PAYMENT_METHODS } from '@/helpers/cash';
import { calculateFee, getProcessingTime } from '@/pages/FundsAddPage/utils';
import { useGetPaymentCalculatedAmount } from '@/query/queries/cash/useGetPaymentCalculatedAmount';
import { createLHVPaymentSession } from '@/store/services/lhvServices';
import IconInfo from '@scramble/design-system/icons/info-circle.svg?react';
import { APP_LINKS } from '@/helpers/links';
import { withRouterBase } from '@/config/routerBase';
import s from './index.module.scss';

const id = MODAL_IDS.review_top_up_modal;

const VALIDATION_SCHEMA = Yup.object().shape({
  has_accepted: Yup.bool().oneOf([true]),
});

export const ReviewTopUpModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ amount, method }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal } = useModal();
  const [loading, setLoading] = useState(false);
  const { data: calculation } = useGetPaymentCalculatedAmount({
    amount,
    payment_method: method.type,
  });

  const feeText = useMemo(() => {
    if (!calculation) return '';
    const totalFee = calculation.amount;
    const fee = calculateFee(totalFee, amount);
    if (fee <= 0) return t('funds:utils.no_fee');
    if (fee > 0) return prettyMoney(fee);
    return '';
  }, [amount, calculation]);


  const onSubmit = async () => {
    try {
      setLoading(true);
      const res = await createLHVPaymentSession({
        payment_method: method.type,
        amount,
        redirect_url: window.location.origin + withRouterBase(APP_LINKS.addFunds),
      });
      if (res && res.success) {
        return window.location.replace(res.data.payment_link);
      }
    } finally {
      setLoading(false);
    }
  };

  const isCardPayment = method?.type === PAYMENT_METHODS.card;

  const { values, setFieldValue, handleSubmit } = useFormik({
    validationSchema: VALIDATION_SCHEMA,
    initialValues: { has_accepted: !isCardPayment },
    onSubmit,
  });

  const content = (
    <form className={s.modal} onSubmit={handleSubmit}>
      <div className={s.header}>
        <div className={s.icon}>
          <img src={method.icon} width={28} height={28} alt="bank" />
        </div>
        <div className="f-headline-2xs f-500">
          {t('modal:review_top_up_modal.title')}
        </div>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          <div className={s.summary}>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_top_up_modal.top_up_amount')}
              </div>
              <div className="f-body-md f-500">
                {prettyMoney(Number(amount))}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_top_up_modal.payment_method')}
              </div>
              <div className="f-body-md f-500">{method.title}</div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_top_up_modal.processing_time')}
              </div>
              <div className="f-body-md f-500">
                {getProcessingTime(method.type)}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_top_up_modal.fee')}
              </div>
              <div className="f-body-md f-500">{feeText}</div>
            </div>
            {calculation?.amount > Number(amount) && (
              <div className={s.row}>
                <div className="f-body-md">
                  {t('modal:review_top_up_modal.payment_amount')}
                </div>
                <div className="f-body-md f-500">
                  {prettyMoney(calculation?.amount)}
                </div>
              </div>
            )}
          </div>
          {isCardPayment && (
            <>
              <InlineAlert
                type="neutral"
                icon={<IconInfo />}
                message={t('modal:review_top_up_modal.final_fee')}
              />
              <div className={s.confirm}>
                <Checkbox
                  checked={values.has_accepted}
                  onChange={e =>
                    setFieldValue('has_accepted', e.target.checked)
                  }
                  id="has_accepted"
                />
                <label htmlFor="has_accepted" className="f-body-sm">
                  {t('modal:review_top_up_modal.confirm_text')}
                </label>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          label={t('modal:review_top_up_modal.cancel')}
          onClick={() => closeModal(id)}
        />
        <Button
          type="submit"
          theme="neutral"
          appearance="filled"
          size="lg"
          disabled={!values.has_accepted || loading}
          label={
            loading
              ? t('modal:review_top_up_modal.processing')
              : t('modal:review_top_up_modal.confirm')
          }
        />
      </div>
    </form>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};